/* eslint-disable no-console */

import camelCaseKeys from 'camelcase-keys';

import config from '../../config';

function offlineResponse() {
  console.log('[DEBUG:API]', 'Something went wrong. Please try again later.');
  return 'Something went wrong. Please try again later.';
}

export type TokenType = string;
export type APIResponse = { response: { entities?: any } };

export function getFullUrl(endpoint: string): string {
  if (endpoint.indexOf('https://') >= 0 || endpoint.indexOf('http://') >= 0) {
    return endpoint;
  }
  return endpoint.indexOf(config.urls.api) === -1
    ? `${config.urls.api}${endpoint}`
    : endpoint;
}

// Fetches an API response and normalizes the result JSON according to schema.
// This makes every API response have the same shape, regardless of how nested it was.
export async function callApi(
  endpoint: string,
  method: string = 'GET',
  headers: HeadersInit = {},
  body: BodyInit = '',
): Promise<APIResponse> {
  const fullUrl = getFullUrl(endpoint);

  const requestHeaders = new Headers(headers);

  const params: RequestInit = {
    method,
    credentials: 'include',
    headers: requestHeaders,
  };

  if (
    method === 'PATCH' ||
    method === 'POST' ||
    method === 'PUT' ||
    method === 'DELETE'
  ) {
    if (body) {
      params.body = body;
    }
  }

  console.log('[DEBUG:API]', fullUrl, method, requestHeaders, body, params);

  const response = await fetch(fullUrl, params);
  if (response.status < 100 || response.status >= 500) {
    throw new Error(offlineResponse());
  }

  if (response.status === 204 || response.status === 205) {
    return { response: {} };
  }

  const json = camelCaseKeys(await response.json(), { deep: true });
  if (!response.ok) {
    throw new Error(JSON.stringify(json));
  }

  return {
    response: {
      entities: json,
    },
  };
}

export async function callApiWithToken(
  endpoint: string,
  token: TokenType,
  method = 'GET',
  headers: HeadersInit = {},
  body: BodyInit = '',
) {
  const headersWithAuthorization: HeadersInit = new Headers(headers);
  if (token) {
    headersWithAuthorization.append('Authorization', `Bearer ${token}`);
  }
  return callApi(endpoint, method, headersWithAuthorization, body);
}

/**
 * Download a file (e.g., PDF) with token authentication.
 * Returns the blob response for file downloads.
 *
 * @param endpoint - API endpoint URL
 * @param token - Authentication token
 * @param filename - Filename to save as (used for download trigger)
 * @returns Promise with blob response
 */
export async function downloadFileWithToken(
  endpoint: string,
  token: TokenType,
  filename: string = 'download.pdf',
): Promise<void> {
  const fullUrl = getFullUrl(endpoint);

  // Debug logging
  console.log('[DEBUG:API]', 'downloadFileWithToken called', {
    endpoint,
    fullUrl,
    token: token ? `${token.substring(0, 20)}...` : 'NO TOKEN',
    filename,
  });

  if (!token) {
    throw new Error('Authentication token is required to download PDF');
  }

  const headers = new Headers();
  headers.append('Authorization', `Bearer ${token}`);

  console.log('[DEBUG:API]', 'Fetch headers:', {
    Authorization: `Bearer ${token.substring(0, 20)}...`,
  });

  const response = await fetch(fullUrl, {
    method: 'GET',
    credentials: 'include',
    headers: headers,
  });

  console.log(
    '[DEBUG:API]',
    'downloadFileWithToken response status:',
    response.status,
  );

  if (response.status === 401) {
    const errorData = await response.json().catch(() => ({}));
    console.error('[DEBUG:API]', 'Authentication failed (401):', errorData);
    throw new Error('Authentication failed. Please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    console.error(
      '[DEBUG:API]',
      'PDF download failed:',
      response.status,
      errorData,
    );
    throw new Error(`PDF download failed: ${response.status}`);
  }

  // Get the blob from the response
  const blob = await response.blob();

  // Create a blob URL and trigger download
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);

  console.log('[DEBUG:API]', 'PDF downloaded successfully:', filename);
}
